
import Vue from "vue";
import { getDatesFromStartEnd } from "@/utils/settlementUtils";
import { db } from "@/utils/dexie";
import moment from "moment";
import MonthControl from "./MonthControl.vue";
import { chartMixin } from "@/mixins/chartMixin";
import {
  filterSettlementResult,
  OrderPathFilter,
} from "@/utils/orderPathFilter";

import { Chart as ChartJS, registerables } from "chart.js";
ChartJS.register(...registerables);

export default Vue.extend({
  mixins: [chartMixin],
  components: {
    MonthControl,
  },
  props: {
    calendarValue: Object as () => moment.Moment,
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data: () => ({
    chart: null as ChartJS | null,
    isRendering: false,
    hasPendingRender: false,
  }),
  watch: {
    calendarValue: {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      immediate: false,
    },
    "$store.state.mall_info": {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      deep: true,
    },
    orderPathFilter: {
      handler() {
        this.$nextTick(() => this.render());
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.render();
    });
  },

  methods: {
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    createOrUpdateChart(ctx: any, config: any) {
      this.destroyChart();
      this.chart = new ChartJS(ctx, config as any) as any;
    },
    async render() {
      if (this.isRendering) {
        this.hasPendingRender = true;
        return;
      }
      this.isRendering = true;

      try {
        const canvasEl = this.$refs.chart as HTMLCanvasElement | undefined;
        if (!canvasEl) return;

        const ctx = canvasEl.getContext("2d");
        if (!ctx) return;

        const shop = this.$store.state.mall_info;
        if (!shop || !shop.mall_id || shop.shop_no === undefined) {
          return;
        }

        const start_date = this.calendarValue
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const end_date = this.calendarValue
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");
        const dates = getDatesFromStartEnd(start_date, end_date);

        let dbItems = await db.results
          .where("[mall_id+shop_no]")
          .equals([shop.mall_id, String(shop.shop_no)])
          .toArray();

        const hasNoItems = dbItems.length === 0;
        const validShopNo = shop.shop_no !== undefined;
        if (hasNoItems && validShopNo && !isNaN(Number(shop.shop_no))) {
          const numItems = await db.results
            .where("[mall_id+shop_no]")
            .equals([shop.mall_id, Number(shop.shop_no)])
            .toArray();
          if (numItems.length > 0) {
            dbItems = numItems;
          }
        }

        const rows = {} as Record<string, any>;
        for (const item of dbItems) {
          if (item && item.date >= start_date && item.date <= end_date) {
            rows[item.date] = filterSettlementResult(
              item,
              this.orderPathFilter
            );
          }
        }
        const dsLink =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--ds-link")
            .trim() || "#0070f3";
        const dsError =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--ds-error")
            .trim() || "#ee0000";
        const dsHairline =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--ds-hairline")
            .trim() || "#ebebeb";

        const labels = dates;
        const datasets = [
          {
            label: "결제",
            data: labels.map((date) => {
              const r = rows[date];
              return r && r.order_count !== undefined ? r.order_count : null;
            }),
            borderColor: dsLink,
            backgroundColor: dsLink,
            borderWidth: 2,
            spanGaps: true,
          },
          {
            label: "환불",
            data: labels.map((date) => {
              const r = rows[date];
              return r && r.refund_count !== undefined ? r.refund_count : null;
            }),
            borderColor: dsError,
            backgroundColor: dsError,
            borderWidth: 2,
            spanGaps: true,
          },
        ];

        const chartLabels = labels.map((l) => moment(l).format("D"));

        const existingChart = ChartJS.getChart(canvasEl);
        if (existingChart) {
          existingChart.destroy();
        }

        this.createOrUpdateChart(canvasEl, {
          type: "line",
          data: {
            labels: chartLabels,
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: {
                grid: {
                  color: dsHairline,
                },
              },
              y: {
                grid: {
                  color: dsHairline,
                },
              },
            },
            plugins: {
              legend: {
                display: true,
                position: "left",
                align: "start",
                labels: {
                  boxWidth: 12,
                  boxHeight: 12,
                  font: {
                    family: "Geist, Inter, sans-serif",
                    size: 11,
                  },
                },
              },
            },
          },
        });
      } catch (e) {
        console.error("DailyCountChart render error:", e);
      } finally {
        this.isRendering = false;
        if (this.hasPendingRender) {
          this.hasPendingRender = false;
          this.$nextTick(() => {
            this.render();
          });
        }
      }
    },
  },
});
